import React from 'react';
import { Card, Button, Empty, Tooltip, Tag } from 'antd';
import { 
  StarOutlined, 
  StarFilled, 
  EyeOutlined, 
  LineChartOutlined,
  BuildOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import Chart from 'react-apexcharts';
import PropTypes from 'prop-types';

const GraficasFavoritasWidget = ({ graficas, onToggleFavorite }) => {
  if (!graficas || graficas.length === 0) {
    return (
      <Card 
        title="Gráficas Favoritas" 
        className="dashboard-widget dashboard-widget--graficas"
      >
        <Empty 
          description="No tienes gráficas favoritas"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const createMiniChart = (sensores) => {
    if (!sensores || sensores.length === 0) return null;

    // Combinar datos de todos los sensores
    const allData = sensores.reduce((acc, sensor) => {
      if (sensor.data && sensor.data.length > 0) {
        const sensorData = sensor.data.map(point => ({
          x: new Date(point.date).getTime(),
          y: point.value,
          sensor: sensor.alias || sensor.nombre
        }));
        acc.push(...sensorData);
      }
      return acc;
    }, []);

    if (allData.length === 0) return null;

    // Ordenar por fecha
    allData.sort((a, b) => a.x - b.x);

    const chartOptions = {
      chart: {
        type: 'line',
        height: 60,
        sparkline: { enabled: true },
        toolbar: { show: false }
      },
      colors: ['#1890ff', '#52c41a', '#faad14', '#ff4d4f'],
      stroke: {
        curve: 'smooth',
        width: 1.5
      },
      xaxis: {
        type: 'datetime'
      },
      yaxis: {
        labels: { show: false }
      },
      tooltip: {
        x: {
          format: 'dd/MM HH:mm'
        },
        y: {
          formatter: (val) => `${val.toFixed(2)}`
        }
      }
    };

    const series = sensores.map((sensor) => ({
      name: sensor.alias || sensor.nombre,
      data: sensor.data ? sensor.data.map(point => ({
        x: new Date(point.date).getTime(),
        y: point.value
      })) : []
    }));

    return (
      <Chart
        options={chartOptions}
        series={series}
        type="line"
        height={60}
      />
    );
  };

  const handleToggleFavorite = (graficaId, currentFavorite) => {
    if (onToggleFavorite) {
      onToggleFavorite(graficaId, !currentFavorite);
    }
  };

  return (
    <Card 
      title="Gráficas Favoritas" 
      className="dashboard-widget dashboard-widget--graficas dashboard-widget--large"
      extra={<StarOutlined />}
    >
      <div className="graficas-grid">
        {graficas.slice(0, 10).map((grafica) => (
          <div key={grafica._id} className="grafica-card">
            <div className="grafica-card__header">
              <div className="grafica-card__title">
                <Tooltip title={grafica.nombre}>
                  <h4>{grafica.nombre}</h4>
                </Tooltip>
                <div className="grafica-card__tags">
                  <Tag size="small" color="blue">
                    {grafica.tipo?.nombre || 'Tipo no disponible'}
                  </Tag>
                  <Tag size="small" color="green">
                    {grafica.tipografica?.nombre || 'Tipo gráfica no disponible'}
                  </Tag>
                </div>
              </div>
              <div className="grafica-card__actions">
                <Tooltip title={grafica.favorita ? "Quitar de favoritos" : "Agregar a favoritos"}>
                  <Button
                    type="text"
                    size="small"
                    icon={grafica.favorita ? <StarFilled /> : <StarOutlined />}
                    onClick={() => handleToggleFavorite(grafica._id, grafica.favorita)}
                    className={grafica.favorita ? 'favorite-active' : ''}
                  />
                </Tooltip>
                <Tooltip title="Ver detalles">
                  <Link to={`/info-grafica/${grafica._id}`}>
                    <Button type="text" size="small" icon={<EyeOutlined />} />
                  </Link>
                </Tooltip>
              </div>
            </div>

            <div className="grafica-card__project">
              <BuildOutlined />
              <span>{grafica.proyecto?.nombre || 'Proyecto no disponible'}</span>
            </div>

            <div className="grafica-card__chart">
              {createMiniChart(grafica.sensores) || (
                <div className="grafica-card__no-data">
                  <LineChartOutlined />
                  <span>Sin datos</span>
                </div>
              )}
            </div>

            <div className="grafica-card__sensors">
              <div className="grafica-card__sensor-count">
                {grafica.sensores.length} sensor{grafica.sensores.length !== 1 ? 'es' : ''}
              </div>
              <div className="grafica-card__sensor-list">
                {grafica.sensores.slice(0, 2).map((sensor) => (
                  <Tooltip key={sensor._id} title={`${sensor.nombre} (${sensor.unidades})`}>
                    <Tag size="small">
                      {sensor.alias || sensor.nombre}
                    </Tag>
                  </Tooltip>
                ))}
                {grafica.sensores.length > 2 && (
                  <Tooltip title={`${grafica.sensores.length - 2} más`}>
                    <Tag size="small">+{grafica.sensores.length - 2}</Tag>
                  </Tooltip>
                )}
              </div>
            </div>

            <div className="grafica-card__footer">
              <span className="grafica-card__date">
                Creada: {formatDate(grafica.createdAt)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {graficas.length > 10 && (
        <div className="graficas-more">
          <Link to="/grafica">
            <Button type="primary" ghost>
              Ver todas las gráficas ({graficas.length})
            </Button>
          </Link>
        </div>
      )}
    </Card>
  );
};

GraficasFavoritasWidget.propTypes = {
  graficas: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      nombre: PropTypes.string.isRequired,
      tipo: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        nombre: PropTypes.string.isRequired
      }).isRequired,
      tipografica: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        nombre: PropTypes.string.isRequired
      }).isRequired,
      proyecto: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        nombre: PropTypes.string.isRequired,
        empresa: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({
            _id: PropTypes.string,
            nombre: PropTypes.string
          })
        ]).isRequired
      }).isRequired,
      sensores: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string.isRequired,
          nombre: PropTypes.string.isRequired,
          alias: PropTypes.string,
          unidades: PropTypes.string.isRequired,
          data: PropTypes.arrayOf(
            PropTypes.shape({
              value: PropTypes.number.isRequired,
              date: PropTypes.string.isRequired
            })
          )
        })
      ).isRequired,
      createdAt: PropTypes.string.isRequired,
      favorita: PropTypes.bool
    })
  ),
  onToggleFavorite: PropTypes.func
};

GraficasFavoritasWidget.defaultProps = {
  graficas: [],
  onToggleFavorite: null
};

export default GraficasFavoritasWidget;
